import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { TokenService } from '../services/token.service';

/**
 * Keeps an authenticated session off the login screen. The counterpart to `LoggedInGuard`, and
 * deliberately the same test in reverse so the two cannot disagree about what "authenticated" means.
 *
 * The check is synchronous and local. `initTokenFromRefreshCookie` is an APP_INITIALIZER, so by the
 * time any route activates this tab has either traded its HttpOnly refresh cookie for a token or it
 * has not - the answer is already known, and asking the server would only add a round trip and a
 * visible flash of the login form before the redirect.
 *
 * No redirect loop: `/` is guarded by `LoggedInGuard`, which heartbeats. If the session turns out to
 * be dead server-side its 401 path runs `AuthService.expireSession`, which clears the token before
 * navigating back here - at which point this guard sees nothing usable and lets the form render.
 *
 * Returning a `UrlTree` rather than calling `router.navigate` is what cancels the in-flight
 * navigation rather than racing it, and `ThemeRouter.createUrlTree` re-attaches the theme prefix for
 * known routes, so `:theme/login` needs no special handling here.
 *
 * The destination is the app root, not a dashboard route: where a user lands is their own
 * `automaticRedirect`/homepage preference, and the shell already owns that decision.
 */
export const AnonymousOnlyGuard: CanActivateFn = (): boolean | UrlTree => {
  const tokenService = inject(TokenService);
  const router = inject(Router);

  if (tokenService.isExpired()) return true;

  return router.createUrlTree(['/']);
};
